/**
 * Battle music.
 *
 * One shared `AudioContext` plays one of three interchangeable tracks per battle.
 * Grassland and Spark are a single loop each. Wild is a unit whose intro plays
 * once and is followed by its loop; the two are scheduled ahead on the audio
 * timeline rather than chained with a timer or an `ended` callback, so there is
 * no gap and no timer to go stale at the join.
 *
 * Signal chain for one playback:
 *
 *   source(s) -> playbackGain (fade in/out) -> masterGain (volume x mute) -> destination
 *
 * The fade gain is per playback rather than shared on purpose. A single shared
 * node would let a new track's fade-in revive an old one that was fading out, or
 * let the old track's fade silence the new one.
 *
 * Decoded buffers are cached, so a track that has been heard once starts
 * immediately the next time. Loads in flight are shared, so the prefetch that
 * runs during the arena transition and the play that follows it download once
 * rather than twice.
 *
 * Nothing here fails quietly: a download, decode or resume problem is logged
 * with the reason and, where the caller can act on it, the promise rejects.
 */

const STORAGE_KEY = 'garaimon.music.v1';

/** Volume for a browser that has never stored a choice. */
const DEFAULT_VOLUME = 0.7;
const DEFAULT_MUTED = false;

/** How long the master gain takes to reach a new volume or mute state. */
const GAIN_CHANGE_SECONDS = 0.05;
/** Fade-in of a newly started track, so it cannot begin on a click. */
const FADE_IN_SECONDS = 0.25;
/** Lead time before a scheduled start, so the start time is never already past. */
const START_LEAD_SECONDS = 0.08;
/** How quickly an outgoing track is silenced when a new one is requested. */
const REPLACE_FADE_SECONDS = 0.2;

export type BattleMusicTrack = 'grassland' | 'spark' | 'wild';

/**
 * The files that make up a track.
 *
 * Each entry is a list of candidate URLs in preference order. Only the first
 * `.ogg` is used today; a list keeps a future AAC/M4A fallback for older iPhones
 * to one added entry, with no change to the loader.
 */
interface TrackAssets {
  /** Played once before the loop. Only Wild has one. */
  intro?: readonly string[];
  loop: readonly string[];
}

/** Built from the deploy base, so the game still works under a sub-path. */
const MUSIC_BASE = `${import.meta.env.BASE_URL}audio/music/`;

const TRACK_ASSETS: Record<BattleMusicTrack, TrackAssets> = {
  grassland: { loop: [`${MUSIC_BASE}battle_grassland_loop.ogg`] },
  spark: { loop: [`${MUSIC_BASE}battle_spark_loop.ogg`] },
  wild: { intro: [`${MUSIC_BASE}battle_wild_intro.ogg`], loop: [`${MUSIC_BASE}battle_wild_loop.ogg`] },
};

const ALL_TRACKS = Object.keys(TRACK_ASSETS) as BattleMusicTrack[];

interface MusicSettings {
  volume: number;
  muted: boolean;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function defaultSettings(): MusicSettings {
  return { volume: DEFAULT_VOLUME, muted: DEFAULT_MUTED };
}

/**
 * Reads the saved volume and mute choice. A missing or unreadable record falls
 * back to the defaults and says so: losing the setting silently would look like
 * the game had ignored the player's choice.
 */
function loadSettings(): MusicSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return defaultSettings();
    const parsed = JSON.parse(stored) as Partial<MusicSettings>;
    return {
      volume: typeof parsed.volume === 'number' && Number.isFinite(parsed.volume) ? clamp01(parsed.volume) : DEFAULT_VOLUME,
      muted: typeof parsed.muted === 'boolean' ? parsed.muted : DEFAULT_MUTED,
    };
  } catch (error) {
    console.warn(`Garaimon: the music settings record could not be read, so volume and mute are back to their defaults. Remove localStorage key "${STORAGE_KEY}" to clear it.`, error);
    return defaultSettings();
  }
}

function saveSettings(settings: MusicSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    // The choice simply will not be remembered. That is worth saying rather than
    // losing without explanation.
    console.warn('Garaimon: the music setting could not be saved, so it will reset on the next visit.', error);
  }
}

let settings = loadSettings();

let context: AudioContext | undefined;
let masterGain: GainNode | undefined;

/** Creates the shared context on first use and applies the saved volume. */
function ensureContext(): AudioContext {
  if (context) return context;
  const scope = globalThis as typeof globalThis & { webkitAudioContext?: typeof AudioContext };
  const Constructor = scope.AudioContext ?? scope.webkitAudioContext;
  if (!Constructor) throw new Error('Garaimon: this browser has no Web Audio support, so battle music cannot play.');
  context = new Constructor();
  masterGain = context.createGain();
  masterGain.connect(context.destination);
  applyMasterGain(true);
  return context;
}

/**
 * Pushes the saved volume and mute state to the master gain. A short ramp is
 * used instead of jumping, because an instant gain change is audible as a click.
 */
function applyMasterGain(immediate = false): void {
  if (!masterGain || !context) return;
  const target = settings.muted ? 0 : settings.volume;
  const now = context.currentTime;
  const gain = masterGain.gain;
  gain.cancelScheduledValues(now);
  if (immediate) {
    gain.setValueAtTime(target, now);
    return;
  }
  gain.setValueAtTime(gain.value, now);
  gain.linearRampToValueAtTime(target, now + GAIN_CHANGE_SECONDS);
}

/**
 * Whether the audio clock is advancing.
 *
 * A helper rather than an inline check because `resume()` can change the state
 * without the compiler knowing: after the first comparison it narrows `state` to
 * the not-running branches, so a later comparison against `'running'` looks
 * impossible even though it is the answer that matters.
 */
function isAudioRunning(ctx: AudioContext): boolean {
  return ctx.state === 'running';
}

/**
 * Asks the browser to run the audio clock. Resolving is how a `play()` inside a
 * tap gesture turns sound on; without it the context stays suspended and nothing
 * is audible, so the outcome is reported rather than assumed.
 */
async function resumeContext(): Promise<boolean> {
  const ctx = context;
  if (!ctx) return false;
  if (isAudioRunning(ctx)) return true;
  try {
    await ctx.resume();
  } catch (error) {
    console.warn('Garaimon: the browser would not start audio. Battle music needs a tap on the screen first.', error);
    return false;
  }
  return isAudioRunning(ctx);
}

const UNLOCK_EVENTS = ['pointerdown', 'touchend', 'keydown'] as const;

let listenersInstalled = false;

function installGlobalListeners(): void {
  if (listenersInstalled) return;
  listenersInstalled = true;
  for (const type of UNLOCK_EVENTS) window.addEventListener(type, onFirstGesture, { passive: true });
  document.addEventListener('visibilitychange', onVisibilityChange);
}

function removeGestureListeners(): void {
  for (const type of UNLOCK_EVENTS) window.removeEventListener(type, onFirstGesture);
}

function removeGlobalListeners(): void {
  removeGestureListeners();
  document.removeEventListener('visibilitychange', onVisibilityChange);
  listenersInstalled = false;
}

/**
 * Runs on the player's first interaction anywhere in the game. Doing this once,
 * globally, means music is not stuck silent if the battle was entered through a
 * control that did not itself unlock audio.
 */
function onFirstGesture(): void {
  void resumeContext().then((running) => {
    if (running) removeGestureListeners();
  });
}

/**
 * Mobile browsers suspend the audio clock when the page is backgrounded (lock
 * screen, incoming call, tab switch), and nothing else brings it back.
 */
function onVisibilityChange(): void {
  if (document.visibilityState !== 'visible') {
    // Desktop browsers leave the audio clock running in a hidden tab, so the
    // music would play on with the battle paused behind the tab. Suspending the
    // clock silences it and keeps the track's position, so it carries on from
    // where it stopped when the page is shown again.
    const ctx = context;
    if (ctx && isAudioRunning(ctx)) {
      void ctx.suspend().catch((error) => console.warn('Garaimon: battle music could not be silenced while the page was hidden.', error));
    }
    return;
  }
  void resumeContext().then((running) => {
    if (!running) console.warn('Garaimon: audio is still suspended after returning to the page, so battle music will resume on the next tap.');
  });
}

/** Decoded buffers, keyed by URL. A URL identifies exactly one file. */
const bufferCache = new Map<string, AudioBuffer>();
/** Loads already in flight, so a prefetch and a play share one download. */
const bufferLoads = new Map<string, Promise<AudioBuffer>>();

async function fetchBuffer(ctx: AudioContext, url: string): Promise<AudioBuffer> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`could not download battle music "${url}" (HTTP ${response.status})`);
  const bytes = await response.arrayBuffer();
  const buffer = await ctx.decodeAudioData(bytes);
  // An unsupported codec can decode to an empty buffer instead of rejecting, and
  // a silent buffer is a bug, not a track.
  if (buffer.length === 0) throw new Error(`battle music "${url}" decoded to an empty buffer`);
  return buffer;
}

function loadBuffer(url: string): Promise<AudioBuffer> {
  const cached = bufferCache.get(url);
  if (cached) return Promise.resolve(cached);
  const inFlight = bufferLoads.get(url);
  if (inFlight) return inFlight;
  const ctx = ensureContext();
  const load = fetchBuffer(ctx, url)
    .then((buffer) => {
      // Guarded so a load that finishes after `disposeAudio()` cannot repopulate
      // a cache that was deliberately cleared.
      if (context === ctx) bufferCache.set(url, buffer);
      return buffer;
    })
    .finally(() => {
      bufferLoads.delete(url);
    });
  bufferLoads.set(url, load);
  return load;
}

/**
 * Loads the first candidate that works. There is one candidate per track today,
 * so a failure ends here; the loop is what makes a future fallback format a
 * data-only change.
 */
async function loadFromCandidates(urls: readonly string[]): Promise<AudioBuffer> {
  let lastError: unknown;
  for (let index = 0; index < urls.length; index += 1) {
    const url = urls[index];
    if (!url) continue;
    try {
      const buffer = await loadBuffer(url);
      if (index > 0) console.warn(`Garaimon: the preferred battle music file was unavailable, so the fallback "${url}" is being used.`);
      return buffer;
    } catch (error) {
      lastError = error;
      if (index < urls.length - 1) console.warn(`Garaimon: battle music "${url}" could not be loaded, so the next available format is being tried.`, error);
    }
  }
  throw lastError instanceof Error ? lastError : new Error('no battle music file could be loaded');
}

async function preloadTrack(track: BattleMusicTrack): Promise<void> {
  const assets = TRACK_ASSETS[track];
  const jobs = [loadFromCandidates(assets.loop)];
  if (assets.intro) jobs.push(loadFromCandidates(assets.intro));
  await Promise.all(jobs);
}

/**
 * Fetches and decodes the music ready for playback. With no argument every track
 * is prepared, which is a testing path: holding every track decoded costs memory
 * that a phone does not need to spend, so normal play names the one track it
 * will use.
 */
export async function preloadBattleMusic(track?: BattleMusicTrack): Promise<void> {
  await Promise.all((track ? [track] : ALL_TRACKS).map(preloadTrack));
}

/** One playback: the fade gain it owns and the sources feeding it. */
interface Playback {
  generation: number;
  gain: GainNode;
  sources: AudioBufferSourceNode[];
}

/** Bumped by every play, stop and dispose. A stale value means "no longer wanted". */
let generation = 0;
let current: Playback | undefined;

export interface PlayBattleMusicOptions {
  /** Play the intro once before the loop. Only Wild has one; default true. */
  playIntro?: boolean;
}

function stopSources(playback: Playback, when: number): void {
  for (const source of playback.sources) {
    try {
      // `stop` is valid after `start` was called with a future time, and that is
      // exactly how a loop scheduled behind an intro is cancelled when the
      // battle ends during the intro.
      source.stop(when);
    } catch (error) {
      console.warn('Garaimon: a battle music source could not be stopped cleanly.', error);
    }
  }
}

/** Silences a playback and releases its nodes once the fade has finished. */
function fadeOut(playback: Playback, fadeSeconds: number): void {
  const ctx = context;
  if (!ctx) return;
  const now = ctx.currentTime;
  const gain = playback.gain.gain;
  // Clear any ramp still in progress and hold the current value first, otherwise
  // that ramp would be applied after this one and override the fade.
  gain.cancelScheduledValues(now);
  gain.setValueAtTime(gain.value, now);
  if (fadeSeconds <= 0) {
    gain.linearRampToValueAtTime(0, now);
    stopSources(playback, now);
    return;
  }
  gain.linearRampToValueAtTime(0, now + fadeSeconds);
  stopSources(playback, now + fadeSeconds);
}

/** Detaches a finished source, and the playback with it once none are left. */
function releaseSource(playback: Playback, source: AudioBufferSourceNode): void {
  source.disconnect();
  const index = playback.sources.indexOf(source);
  if (index >= 0) playback.sources.splice(index, 1);
  if (playback.sources.length > 0) return;
  playback.gain.disconnect();
  if (current === playback) current = undefined;
}

/**
 * Starts a track. Grassland and Spark begin their loop straight away; Wild plays
 * its intro and then its loop, both scheduled against `AudioContext.currentTime`
 * so the join is exact. Sources are created fresh every time, because an
 * `AudioBufferSourceNode` cannot be restarted.
 */
export async function playBattleMusic(track: BattleMusicTrack, options: PlayBattleMusicOptions = {}): Promise<void> {
  const ctx = ensureContext();
  const myGeneration = (generation += 1);

  // Silence whatever is playing before the new track's download begins, so a
  // rapid change never leaves two tracks audible at once.
  const previous = current;
  if (previous) {
    current = undefined;
    fadeOut(previous, REPLACE_FADE_SECONDS);
  }

  const assets = TRACK_ASSETS[track];
  const wantsIntro = assets.intro !== undefined && options.playIntro !== false;
  const [loopBuffer, introBuffer] = await Promise.all([
    loadFromCandidates(assets.loop),
    wantsIntro && assets.intro ? loadFromCandidates(assets.intro) : Promise.resolve(undefined),
  ]);

  // A newer play, a stop, or a dispose happened while the buffers were loading.
  // The battle this was for no longer wants music, so nothing is started; this
  // is what stops a late-finishing load from resurrecting a finished battle.
  if (myGeneration !== generation) return;

  const master = masterGain;
  if (!master) return;

  const started = await resumeContext();
  if (!started) {
    console.warn('Garaimon: battle music is ready but the browser has not allowed audio to start, so it will stay silent until the next tap.');
    return;
  }

  const now = ctx.currentTime;
  const startAt = now + START_LEAD_SECONDS;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(1, startAt + FADE_IN_SECONDS);
  gain.connect(master);

  const playback: Playback = { generation: myGeneration, gain, sources: [] };
  current = playback;

  if (introBuffer) {
    const introSource = ctx.createBufferSource();
    introSource.buffer = introBuffer;
    introSource.connect(gain);
    introSource.onended = () => releaseSource(playback, introSource);
    introSource.start(startAt);
    playback.sources.push(introSource);
  }

  const loopSource = ctx.createBufferSource();
  loopSource.buffer = loopBuffer;
  // The whole file is the loop, so it repeats from its start to its end.
  loopSource.loop = true;
  loopSource.loopStart = 0;
  loopSource.loopEnd = loopBuffer.duration;
  loopSource.connect(gain);
  loopSource.onended = () => releaseSource(playback, loopSource);
  loopSource.start(introBuffer ? startAt + introBuffer.duration : startAt);
  playback.sources.push(loopSource);
}

/**
 * Fades the current track out and releases it. Ending during the intro is
 * covered because the loop is stopped by the same call, even though its start
 * time is still in the future. A load still in flight is discarded too, so
 * retreating mid-download cannot produce music with no battle left to stop it.
 */
export function stopBattleMusic(fadeSeconds = 0.5): void {
  generation += 1;
  const playback = current;
  if (!playback) return;
  current = undefined;
  fadeOut(playback, fadeSeconds);
}

/** Sets the music volume from 0 to 1 and remembers it. */
export function setMusicVolume(valueFrom0To1: number): void {
  if (!Number.isFinite(valueFrom0To1)) {
    console.warn(`Garaimon: "${valueFrom0To1}" is not a usable music volume, so the current volume is unchanged.`);
    return;
  }
  settings = { ...settings, volume: clamp01(valueFrom0To1) };
  saveSettings(settings);
  applyMasterGain();
}

/**
 * Mutes or unmutes without stopping playback, so unmuting rejoins the track
 * where it is rather than restarting it.
 */
export function setMusicMuted(muted: boolean): void {
  settings = { ...settings, muted };
  saveSettings(settings);
  applyMasterGain();
}

export function isMusicMuted(): boolean {
  return settings.muted;
}

/** Flips mute and returns the new state, for a control that reflects it. */
export function toggleMusicMuted(): boolean {
  setMusicMuted(!settings.muted);
  return settings.muted;
}

let lastPickedTrack: BattleMusicTrack | undefined;

/** Picks the track for a battle, avoiding the same one twice in a row. */
export function pickRandomBattleTrack(): BattleMusicTrack {
  const choices = ALL_TRACKS.length > 1 && lastPickedTrack ? ALL_TRACKS.filter((track) => track !== lastPickedTrack) : ALL_TRACKS;
  const chosen = choices[Math.floor(Math.random() * choices.length)] ?? 'grassland';
  lastPickedTrack = chosen;
  return chosen;
}

/**
 * Releases everything and closes the audio clock. Closing a context cannot be
 * undone, so this is for explicit teardown and tests, not for `pagehide`: doing
 * it there would leave audio permanently dead after a back/forward-cache restore.
 */
export function disposeAudio(): void {
  generation += 1;
  const playback = current;
  current = undefined;
  if (playback) fadeOut(playback, 0);
  removeGlobalListeners();
  const ctx = context;
  context = undefined;
  masterGain = undefined;
  bufferCache.clear();
  bufferLoads.clear();
  if (!ctx) return;
  void ctx.close().catch((error) => {
    console.warn('Garaimon: the audio system could not be closed cleanly.', error);
  });
}

/**
 * Unlocks audio from inside a user gesture without needing a track to play.
 *
 * This never throws. It is called as the first step of opening a battle, and
 * audio is optional: a browser without Web Audio support should still play the
 * game, just silently, with the reason visible in the console.
 */
export function unlockAudio(): void {
  try {
    ensureContext();
  } catch (error) {
    console.warn('Garaimon: audio is unavailable, so battle music is off. The battle itself is unaffected.', error);
    return;
  }
  installGlobalListeners();
  void resumeContext();
}
